import React, { ReactNode, useState, useRef, useEffect, useMemo } from 'react'
import { Flex, Box, SystemProps } from '@storyofams/react-ui'
import { motion, AnimatePresence } from 'framer-motion'
import { defineMessages, useIntl } from 'react-intl'
import { Button, Text, CartButton, Heading } from '~components'
import { shopifyDiscountPriceFactor } from '~config'
import { Badge } from '../../common/Cart/Badge'
import { position } from 'styled-system'

const messages = defineMessages({
  minimumNotReach: 'Select minimum 8 meals to continue',
  minimumFreeShipNotReach: '2 more products for free shipping',
  freeShipping: 'You got free shipping!',
})
const giftCardTag = 'gift_card'

export const SummaryBarContent = ({
  quantity,
  cart,
  cartOpen,
  setCartOpen,
  buttonProps,
  children,
  style,
}: {
  style?: any
  children?: any
  quantity?: any
  cart?: any
  cartOpen: boolean
  setCartOpen: Function
  buttonProps?: any
} & SystemProps) => {
  const intl = useIntl()
  const [sixItemsCost, setSixItemsCost] = useState(0)
  const [freeShipingProgress, setFreeShipingProgress] = useState(0)
  const totalAmount = !!cart ? Number(cart?.cost?.totalAmount?.amount) : 0
  const quantityWithoutGiftCard = useMemo(() => {
    let newQuantity = quantity
    const lineItems = cart?.lines?.edges.map(el => el.node)

    if (lineItems) {
      lineItems.forEach(lineItem => {
        const productData = lineItem?.merchandise?.product

        if (productData && !!productData?.tags.find(el => el == giftCardTag)) {
          newQuantity = newQuantity - lineItem.quantity
        }
      })
    }

    return newQuantity
  }, [cart])

  const totalAmountWithoutGiftCard = useMemo(() => {
    let newTotalAmount = totalAmount
    const lineItems = cart?.lines?.edges.map(el => el.node)

    if (lineItems) {
      lineItems.forEach(lineItem => {
        const productData = lineItem?.merchandise?.product

        if (productData && !!productData?.tags.find(el => el == giftCardTag)) {
          const variantPrice = lineItem?.merchandise?.price?.amount
            ? Number(lineItem?.merchandise?.price?.amount)
            : 0

          if (variantPrice) {
            const lineItemsTotalAmount = variantPrice * lineItem.quantity

            newTotalAmount = newTotalAmount - lineItemsTotalAmount
          }
        }
      })
    }

    return newTotalAmount
  }, [cart])

  useEffect(() => {
    calculateFreeShipingProgress()
  }, [])

  useEffect(() => {
    calculateFreeShipingProgress()
  }, [cart, quantity])

  const calculateFreeShipingProgress = () => {
    if (quantityWithoutGiftCard == 8) {
      const totalAmount = totalAmountWithoutGiftCard
      setSixItemsCost(totalAmount)
    }

    if (quantityWithoutGiftCard >= 8) {
      const totalAmount = totalAmountWithoutGiftCard
      let sixItemsCostLocal = sixItemsCost

      if (quantityWithoutGiftCard > 8 && sixItemsCost == 0) {
        sixItemsCostLocal =
          8 * (totalAmountWithoutGiftCard / quantityWithoutGiftCard)
      }

      setFreeShipingProgress(
        sixItemsCostLocal * shopifyDiscountPriceFactor > 60
          ? 100
          : Math.round(
              (totalAmountWithoutGiftCard * shopifyDiscountPriceFactor -
                sixItemsCostLocal * shopifyDiscountPriceFactor) /
                ((60 - sixItemsCostLocal * shopifyDiscountPriceFactor) / 100),
            ),
      )
    } else {
      setFreeShipingProgress(0)
    }
  }

  const ProgressBar = () => {
    return (
      <Flex
        width={['100%', '100%', 'auto']}
        flexDirection={'column'}
        alignItems={'center'}
      >
        <Text
          mb={'10px'}
          fontSize={['11px', 2, 2]}
          lineHeight={'1em'}
          minHeight={'1em'}
          textAlign={{ all: 'center', md: 'left' }}
          color={'#21857F'}
        >
          {totalAmountWithoutGiftCard * 0.9 < 60
            ? quantityWithoutGiftCard < 8
              ? intl.formatMessage(messages.minimumNotReach)
              : intl.formatMessage(messages.minimumFreeShipNotReach)
            : intl.formatMessage(messages.freeShipping)}
        </Text>
        <Flex width={['100%', '100%', 'auto']}>
          <Box
            display={'flex'}
            width={['100%', '100%', '196px']}
            style={{
              background: '#D8D8D9',
              backgroundImage: `linear-gradient(90deg, ${
                quantityWithoutGiftCard < 8 ? '#21857F' : ''
              }${
                quantityWithoutGiftCard >= 8 && freeShipingProgress < 100
                  ? '#21857F'
                  : ''
              }${freeShipingProgress >= 100 ? '#21857F' : ''} ${Math.round(
                (100 / 8) * quantityWithoutGiftCard,
              )}%,transparent 0%)`,
              height: '20px',
              borderRadius: '50px 0px 0px 50px',
            }}
          />
          <Box
            style={{
              marginLeft: '4px',
              background: '#D8D8D9',
              backgroundImage: `linear-gradient(90deg, ${
                quantityWithoutGiftCard < 8 ? '#21857F' : ''
              }${
                quantityWithoutGiftCard >= 8 && freeShipingProgress < 100
                  ? '#21857F'
                  : ''
              }${freeShipingProgress >= 100 ? '#21857F' : ''} ${
                quantityWithoutGiftCard >= 8 ? freeShipingProgress : 0
              }%,transparent 0%)`,
              width: '64px',
              height: '20px',
              borderRadius: '0px 50px 50px 0px',
            }}
          />
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      py={[1.5, 1]}
      px={[3, 6, 10]}
      pt={['20px', '20px', 1]}
      mx="auto"
      width="100%"
      maxWidth="maxWidth"
      flexDirection={'row'}
      style={style ? style : {}}
    >
      {children ? children : ''}
      <Flex
        width={['100%', '100%', 'auto']}
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        className="fixed-bar"
        borderRight={{ all: 'none', md: '2px solid #21857F' }}
      >
        <Flex
          width={['100%', '100%', 'auto']}
          flex={1}
          justifyContent="space-between"
          alignItems="center"
        >
          {
            <AnimatePresence>
              <motion.div
                style={{ width: '100%' }}
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeInOut', duration: 0.24 }}
              >
                <Flex
                  width={['100%', '100%', 'auto']}
                  justifyContent={'space-between'}
                >
                  <Box
                    display={'flex'}
                    mr={{ all: 0, md: 6 }}
                    mb={{ all: 1, md: 0 }}
                    minHeight={{ all: '1em', md: '59px' }}
                    alignItems={'center'}
                    flexDirection={['column-reverse', 'column-reverse', 'row']}
                    gap={['2px,', '2px', '10px']}
                    position={'relative'}
                  >
                    <Box
                      display={['none', 'none', 'inline-flex']}
                      position={'relative'}
                    >
                      <CartButton
                        darkIcon
                        customIconWidth={'41px'}
                        quantity={quantity}
                        cartOpen={cartOpen}
                        toggleCartOverlay={() => setCartOpen(!cartOpen)}
                        setCartOpen={() => setCartOpen(true)}
                        backgroundColor={
                          quantityWithoutGiftCard >= 8 ? '#269B1C' : null
                        }
                      />
                    </Box>

                    <Button
                      display={'flex'}
                      variant={'unstyled'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      position={['absolute', 'absolute', 'relative']}
                      top={['-50px', '-50px', 'auto']}
                      left={[0, 0, 'auto']}
                      onClick={() => setCartOpen(!cartOpen)}
                      bg={['white', 'white', 'transparent']}
                      p={[1, 1, 0]}
                      borderRadius={'8px'}
                    >
                      <Flex ml={[0, 0, 8]} alignItems={'center'} gap={'10px'}>
                        <Badge
                          display={['flex', 'flex', 'none']}
                          position="absolute"
                          bg={'#21857F'}
                          right={'-6px'}
                          top={'-6px'}
                          border={'2px solid #FFFFFF'}
                          width={'18px'}
                          height={'18px'}
                          alignItems={'center'}
                        >
                          <Heading
                            as="h4"
                            variant="sh4"
                            fontSize={'9px'}
                            lineHeight={'100%'}
                            color={'#FFFFFF'}
                          >
                            {quantity}
                          </Heading>
                        </Badge>
                        <Flex>
                          <Heading as="h4" variant="sh4" mr={2}>
                            Summary
                          </Heading>
                          {cartOpen ? (
                            <svg
                              width="14"
                              height="9"
                              viewBox="0 0 14 9"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 1.4376L7 7.4376L13 1.4376"
                                stroke="#1B1819"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="14"
                              height="9"
                              viewBox="0 0 14 9"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13 7.4375L7 1.4375L1 7.4375"
                                stroke="#1B1819"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </Flex>
                      </Flex>
                    </Button>
                  </Box>
                  <Box
                    display={['flex', 'flex', 'none']}
                    flexDirection="column"
                    ml={[0, 0, '26px']}
                    mr={[0, 0, '26px']}
                    marginBottom={'0'}
                    alignItems={'center'}
                    justifyContent={['center', 'center', 'auto']}
                    width={['100%', '100%', 'auto']}
                  >
                    <ProgressBar />
                  </Box>
                </Flex>
              </motion.div>
            </AnimatePresence>
          }
        </Flex>
      </Flex>
      <Flex
        width={{ all: '100%', md: 'auto' }}
        flexDirection={'row'}
        alignItems={{ all: 'center', md: 'normal' }}
      >
        <Box
          display={['none', 'none', 'flex']}
          flexDirection="column"
          ml={[0, '26px', '26px']}
          mr={[0, '26px', '26px']}
          marginBottom={{ all: '16px', md: '0' }}
          alignItems={'center'}
        >
          <ProgressBar />
        </Box>
        <Box
          width={{ all: '100%', md: 'auto' }}
          ml={['13px', '13px', 10]}
          textAlign={'center'}
        >
          {buttonProps && (
            <Button
              {...buttonProps}
              mr={['auto']}
              width={['100%', '100%', 'auto']}
              fontSize={['12px', '16px', '16px']}
              letterSpacing={'1.1px'}
              disabled={
                (quantityWithoutGiftCard < 8 && quantityWithoutGiftCard > 0) ||
                (quantityWithoutGiftCard == 0 && quantity == 0)
              }
            >
              {buttonProps.label}
            </Button>
          )}
        </Box>
      </Flex>
    </Flex>
  )
}
